<template>
  <div class="page-wrapper">
    <!-- Global Loading State -->
    <LoadingState 
      v-if="isGlobalLoading"
      :message="loadingMessage"
      subtitle="Bitte warten Sie einen Moment"
    />

    <!-- Main Content -->
    <template v-else>
      <Header />
      
      <!-- Status Banner -->
      <v-alert
        v-if="statusMessage"
        :type="statusType"
        dismissible
        @input="clearStatus"
        class="status-banner"
      >
        {{ statusMessage }}
      </v-alert>

      <!-- Main Grid Container -->
      <div class="tables-container">
      <!-- Categories Table -->
      <div class="table-column categories-column">
        <div class="table-card">
          <div class="table-header primary white--text">
            <v-icon small class="mr-2">category</v-icon>
            <span>Kategorien ({{ categories.length }})</span>
            <v-spacer></v-spacer>
            <v-btn
              small
              color="white"
              outlined
              @click="categoryDialog = true"
              :disabled="isCreatingCategory"
            >
              <v-icon left small>add</v-icon>
              Verwalten
            </v-btn>
          </div>
          
          <div class="table-body">
            <SimpleLoader v-if="isLoadingCategories && !isInitialLoad" loading-text="Lade Kategorien..." />

            <div v-else-if="categories.length === 0" class="empty-state">
              <v-icon color="grey lighten-1">category</v-icon>
              <p class="mt-2">Keine Kategorien</p>
            </div>

            <div v-else class="categories-scroll">
              <v-card
                v-for="category in categories"
                :key="category.id"
                class="category-item"
                outlined
                @click="editCategory(category)"
              >
                <v-card-text class="pa-2">
                  <div class="category-name">
                    <v-icon x-small class="mr-1" color="primary">label</v-icon>
                    <span>{{ category.name }}</span>
                    <v-chip v-if="category.shortName" x-small color="grey lighten-2" class="ml-1">
                      {{ category.shortName }}
                    </v-chip>
                    <v-chip
                      v-if="category.supportsDesigns !== false"
                      x-small
                      color="success"
                      dark
                      class="ml-1"
                    >
                      <v-icon x-small left>mdi-palette</v-icon>
                      Designs
                    </v-chip>
                    <v-chip
                      v-else
                      x-small
                      color="grey"
                      dark
                      class="ml-1"
                    >
                      <v-icon x-small left>mdi-palette-outline</v-icon>
                      Keine Designs
                    </v-chip>
                    <v-spacer></v-spacer>
                    <v-icon x-small color="grey lighten-1">edit</v-icon>
                  </div>
                  
                  <div v-if="category.colors && category.colors.length > 0" class="category-meta">
                    <span class="meta-label">Farben:</span>
                    <v-chip
                      v-for="(color, index) in category.colors.slice(0, 3)"
                      :key="index"
                      x-small
                      color="info"
                      class="mr-1"
                    >
                      {{ color }}
                    </v-chip>
                    <span v-if="category.colors.length > 3" class="text-caption">+{{ category.colors.length - 3 }}</span>
                  </div>
                  
                  <div v-if="category.sizes && category.sizes.length > 0" class="category-meta">
                    <span class="meta-label">Größen:</span>
                    <v-chip
                      v-for="(size, index) in category.sizes.slice(0, 3)"
                      :key="index"
                      x-small
                      color="secondary"
                      class="mr-1"
                    >
                      {{ size }}
                    </v-chip>
                    <span v-if="category.sizes.length > 3" class="text-caption">+{{ category.sizes.length - 3 }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </div>

      <!-- SKU Table -->
      <div class="table-column skus-column">
        <div class="table-card">
          <div class="table-header success white--text">
            <v-icon small class="mr-2">inventory_2</v-icon>
            <span>Design IDs ({{ uniqueDesignIdsCount }})</span>
            <v-spacer></v-spacer>
            <v-btn
              small
              color="white"
              outlined
              @click="openNewDesignIdDialog"
              :disabled="isCreating"
            >
              <v-icon left small>add</v-icon>
              Neue Design ID
            </v-btn>
          </div>

          <div class="table-body">
            <SkuList
              :skus="skuList"
              :teein-skus="teeinSkuList"
              :all-categories="categories"
              :loading="isLoading && !isInitialLoad"
              @view-details="viewSkuDetails"
              @update-candidate-status="updateSkuCandidateStatus"
              @show-message="showMessage"
              @add-category-to-design="handleAddCategoryToDesign"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- SKU Generation Dialog - Hidden for now -->
    <!-- Will be reimplemented in a future task -->
    <!--
    <v-dialog v-model="skuDialog" max-width="600" persistent>
      ...
    </v-dialog>
    -->

    <!-- Category Create/Edit Dialog - Multi-Step -->
    <v-dialog v-model="categoryDialog" max-width="700" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left dark>{{ isEditMode ? 'edit' : 'add_circle' }}</v-icon>
          {{ categoryDialogTitle }}
        </v-card-title>
        
        <!-- Step Indicator -->
        <v-stepper v-model="categoryStep" flat class="elevation-0" v-if="isEditMode && categoryHasSkus">
          <v-stepper-header>
            <v-stepper-step :complete="categoryStep > 1" step="1">Bearbeiten</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="categoryStep > 2" step="2">Fehlende SKUs</v-stepper-step>
          </v-stepper-header>
        </v-stepper>

        <v-card-text class="pt-4">
          <!-- Step 1: Category Form -->
          <div v-show="categoryStep === 1">
              <v-form ref="categoryForm" v-model="categoryFormValid">
                <v-text-field
                  v-model="newCategory.name"
                  label="Kategoriename *"
                  :rules="[v => !!v || 'Name ist erforderlich']"
                  outlined
                  dense
                  class="mb-3"
                ></v-text-field>
                
                <v-text-field
                  v-model="newCategory.shortName"
                  label="Kurzname (für SKU-Präfix)"
                  hint="Max. 10 Zeichen, nur A-Z, a-z, 0-9, _ und -"
                  :rules="shortNameRules"
                  :counter="10"
                  outlined
                  dense
                  class="mb-3"
                  @input="sanitizeShortName"
                ></v-text-field>
                
                <v-combobox
                  v-model="newCategory.colors"
                  label="Farben"
                  hint="Drücken Sie Enter um eine Farbe hinzuzufügen"
                  multiple
                  chips
                  deletable-chips
                  outlined
                  dense
                  class="mb-3"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      small
                      close
                      color="info"
                      @click:close="removeColor(index)"
                    >
                      {{ item }}
                    </v-chip>
                  </template>
                </v-combobox>
                
                <v-combobox
                  v-model="newCategory.sizes"
                  label="Größen"
                  hint="Drücken Sie Enter um eine Größe hinzuzufügen"
                  multiple
                  chips
                  deletable-chips
                  outlined
                  dense
                  class="mb-3"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      small
                      close
                      color="secondary"
                      @click:close="removeSize(index)"
                    >
                      {{ item }}
                    </v-chip>
                  </template>
                </v-combobox>

                <v-checkbox
                  v-model="newCategory.supportsDesigns"
                  label="Unterstützt Design IDs"
                  hint="Deaktivieren für Produkte wie Gravuren, die keine Design IDs verwenden"
                  persistent-hint
                  color="primary"
                  dense
                >
                  <template v-slot:label>
                    <div>
                      <span>Unterstützt Design IDs</span>
                      <v-tooltip top max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon small class="ml-1" v-bind="attrs" v-on="on">info</v-icon>
                        </template>
                        <span>Deaktivieren Sie dies für Produkte wie Gravuren, die keine Design IDs verwenden. Diese Kategorie wird dann in der Design ID Übersicht ausgeblendet.</span>
                      </v-tooltip>
                    </div>
                  </template>
                </v-checkbox>
              </v-form>
            </div>

            <!-- Step 2: Missing SKUs Analysis -->
            <div v-show="categoryStep === 2">
              <div v-if="isAnalyzingCategory" class="text-center py-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <p class="mt-3">Analysiere fehlende SKUs...</p>
              </div>

              <div v-else-if="affectedDesignIds.length === 0">
                <v-alert type="success" dense>
                  <strong>Keine fehlenden SKUs gefunden!</strong>
                  <div class="text-caption mt-1">Alle bestehenden Design IDs sind vollständig.</div>
                </v-alert>
              </div>

              <div v-else>
                <v-alert type="warning" border="left" dense class="mb-3">
                  <strong>{{ affectedDesignIds.length }} Design ID(s) betroffen</strong>
                  <div class="text-caption mt-1">
                    Die Kategorie-Änderungen würden fehlende SKUs für diese Design IDs verursachen.
                  </div>
                </v-alert>

                <div class="mb-3">
                  <div class="text-subtitle-2 mb-2">Design IDs zum Vervollständigen auswählen:</div>
                  <v-checkbox
                    v-for="designId in affectedDesignIds"
                    :key="designId.id"
                    v-model="selectedDesignIdsToFill"
                    :value="designId.id"
                    hide-details
                    dense
                    class="mt-0"
                  >
                    <template v-slot:label>
                      <div class="d-flex align-center justify-space-between" style="width: 100%;">
                        <span><strong>{{ designId.id }}</strong></span>
                        <v-chip x-small color="warning" class="ml-2">
                          {{ designId.missing }} fehlend
                        </v-chip>
                      </div>
                    </template>
                  </v-checkbox>
                </div>

                <v-checkbox
                  v-model="fillWithTeeingInWorkflow"
                  label="Auch TEEIN-Varianten für fehlende SKUs erstellen"
                  hide-details
                  dense
                  color="warning"
                ></v-checkbox>
              </div>
            </div>
          </v-card-text>
          
          <v-card-actions>
            <v-btn
              text
              @click="categoryStepBack"
              v-if="categoryStep === 2 && isEditMode && categoryHasSkus"
              :disabled="isCreatingCategory"
            >
              <v-icon left>arrow_back</v-icon>
              Zurück
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeCategoryDialog"
              :disabled="isCreatingCategory"
            >
              Abbrechen
            </v-btn>
            <v-btn
              color="primary"
              @click="categoryStepNext"
              :loading="isCreatingCategory || isAnalyzingCategory"
              :disabled="!categoryFormValid"
            >
              {{ categoryNextButtonText }}
            </v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- New Design ID Dialog -->
    <v-dialog v-model="newDesignIdDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="success white--text">
          <v-icon left dark>add_circle</v-icon>
          Neue Design ID erstellen
        </v-card-title>

        <v-card-text class="pt-4">
          <p class="text-body-2 mb-4">
            Wählen Sie eine Kategorie für die neue Design ID.
            Alle Farb- und Größenkombinationen werden automatisch erstellt.
          </p>

          <v-select
            v-model="selectedCategoryForNewDesign"
            :items="categories.filter(c => c.supportsDesigns !== false)"
            item-text="name"
            item-value="id"
            label="Kategorie auswählen *"
            outlined
            dense
            :rules="[v => !!v || 'Kategorie ist erforderlich']"
          >
            <template v-slot:selection="{ item }">
              <v-chip small color="secondary" dark>
                {{ item.name }}
              </v-chip>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex flex-column py-2">
                <span class="font-weight-bold">{{ item.name }}</span>
                <span class="text-caption grey--text">
                  {{ item.colors.length }} Farben × {{ item.sizes.length }} Größen = {{ item.colors.length * item.sizes.length }} SKUs
                </span>
              </div>
            </template>
          </v-select>

          <v-checkbox
            v-model="includeTeeinForNewDesign"
            label="TEEIN Varianten erstellen"
            dense
            hide-details
            class="mt-2"
          >
            <template v-slot:label>
              <span class="text-body-2">
                <v-icon small color="info" class="mr-1">mdi-tag-multiple</v-icon>
                TEEIN Varianten erstellen
              </span>
            </template>
          </v-checkbox>
          <p class="text-caption grey--text mt-1 ml-8 mb-4">
            Erstellt zusätzliche SKUs mit dem TEEIN-Flag (z.B. acryl_nachtlicht_teein_D100)
          </p>

          <!-- File Upload Section -->
          <v-divider class="my-4"></v-divider>
          <div class="file-upload-section">
            <p class="text-body-2 font-weight-medium mb-2">
              <v-icon small class="mr-1">mdi-image</v-icon>
              Design-Bild hochladen (optional)
            </p>
            <p class="text-caption grey--text mb-3">
              Laden Sie ein Bild für diese Design ID hoch. Erlaubte Formate: JPG, PNG, GIF, WebP, SVG (max. 10MB)
            </p>

            <v-file-input
              v-model="newDesignImage"
              accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
              label="Bild auswählen"
              prepend-icon="mdi-camera"
              outlined
              dense
              show-size
              :rules="imageRules"
              @change="onImageSelected"
              clearable
            >
              <template v-slot:selection="{ text }">
                <v-chip small color="primary" dark>
                  <v-icon left small>mdi-file-image</v-icon>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>

            <!-- Image Preview -->
            <div v-if="imagePreviewUrl" class="image-preview mt-3">
              <v-img
                :src="imagePreviewUrl"
                max-height="200"
                max-width="100%"
                contain
                class="rounded"
              ></v-img>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeNewDesignIdDialog"
            :disabled="isCreating"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            @click="createNewDesignId"
            :loading="isCreating"
            :disabled="!selectedCategoryForNewDesign"
          >
            <v-icon left small>add</v-icon>
            Design ID erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Expand Design ID Dialog -->
    <v-dialog v-model="expandDesignDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left dark>mdi-package-variant-plus</v-icon>
          Kategorie zu Design ID hinzufügen
        </v-card-title>

        <v-card-text class="pt-4" v-if="expandDesignData">
          <v-alert type="info" dense class="mb-3">
            <strong>Design ID:</strong> {{ expandDesignData.designId }}
          </v-alert>

          <p class="text-body-1 mb-2">
            Möchten Sie die Kategorie <strong>{{ expandDesignData.categoryName }}</strong>
            zu dieser Design ID hinzufügen?
          </p>

          <p class="text-body-2 grey--text">
            Alle fehlenden Farb- und Größenkombinationen werden automatisch erstellt.
          </p>

          <v-checkbox
            v-model="includeTeeinForExpandDesign"
            label="TEEIN Varianten erstellen"
            dense
            hide-details
            class="mt-3"
          >
            <template v-slot:label>
              <span class="text-body-2">
                <v-icon small color="info" class="mr-1">mdi-tag-multiple</v-icon>
                TEEIN Varianten erstellen
              </span>
            </template>
          </v-checkbox>
          <p class="text-caption grey--text mt-1 ml-8">
            Erstellt zusätzliche SKUs mit dem TEEIN-Flag
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeExpandDesignDialog"
            :disabled="isCreating"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmExpandDesign"
            :loading="isCreating"
          >
            <v-icon left small>check</v-icon>
            Ja, hinzufügen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="650">
      <v-card v-if="selectedSku">
        <v-card-title class="primary white--text">
          <v-icon left dark>info</v-icon>
          SKU Details
        </v-card-title>
        
        <v-card-text class="pt-4 pb-2">
          <div class="detail-section">
            <div class="detail-row">
              <span class="detail-label">SKU:</span>
              <span class="detail-value font-weight-bold">{{ selectedSku.sku }}</span>
            </div>
            
            <div class="detail-row editable-row">
              <span class="detail-label">Kategorie:</span>
              <div class="detail-value-editable">
                <v-select
                  v-model="selectedSkuCategory"
                  :items="categories.map(c => ({ text: c.name, value: c.id }))"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  class="category-select-compact"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip small color="secondary" dark>
                      {{ item.text }}
                    </v-chip>
                  </template>
                </v-select>
              </div>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">Design ID:</span>
              <span class="detail-value">{{ selectedSku.designId }}</span>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">Flags:</span>
              <div class="detail-value">
                <v-chip
                  v-for="flag in selectedSku.flags"
                  :key="flag"
                  x-small
                  color="info"
                  class="mr-1"
                >
                  {{ flag }}
                </v-chip>
                <span v-if="!selectedSku.flags || selectedSku.flags.length === 0">
                  Keine Flags
                </span>
              </div>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">Erstellt am:</span>
              <span class="detail-value">{{ formatDate(selectedSku.createdAt) }}</span>
            </div>
          </div>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="detailDialog = false">
            Schließen
          </v-btn>
          <v-btn 
            v-if="hasSkuChanges"
            color="primary"
            @click="saveSkuChanges"
            :loading="isSavingSku"
          >
            <v-icon left small>save</v-icon>
            Änderungen speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </template>
  </div>
</template>

<script>
import Header from "../../header";
import CategorySelect from "./components/CategorySelect.vue";
import SkuList from "./components/SkuList.vue";
import SimpleLoader from "@/components/Loading/SimpleLoader.vue";
import LoadingState from "@/views/private/shipping/shared/LoadingState.vue";
import WarehouseService from "@/services/WarehouseService";

export default {
  name: "skuManagement",
  components: {
    Header,
    CategorySelect,
    SkuList,
    SimpleLoader,
    LoadingState
  },
  data: () => ({
    // SKU State
    selectedCategory: null,
    skuFlags: [],  // Array for checkbox values
    skuList: [],
    teeinSkuList: [], // TEEIN SKUs grouped by Design ID
    lastCreatedSkus: [],
    lastCreatedDesignId: null,
    selectedSku: null,
    sessionCreated: 0,

    // Category State
    categories: [],
    newCategory: {
      name: '',
      shortName: '',
      colors: [],
      sizes: [],
      supportsDesigns: true // Default to true
    },
    originalCategory: null, // Store original for comparison
    editingCategoryId: null,
    isEditMode: false,
    categoryStep: 1,
    affectedDesignIds: [],
    selectedDesignIdsToFill: [],
    fillWithTeeingInWorkflow: false,
    isAnalyzingCategory: false,

    // UI State
    isLoading: false,
    isCreating: false,
    isLoadingCategories: false,
    isCreatingCategory: false,
    detailDialog: false,
    categoryDialog: false,
    skuDialog: false,
    categoryFormValid: false,
    statusMessage: null,
    statusType: 'success',
    isInitialLoad: true,
    
    // SKU Edit State
    selectedSkuCategory: null,
    isSavingSku: false,

    // New Design ID Dialog State
    newDesignIdDialog: false,
    selectedCategoryForNewDesign: null,
    includeTeeinForNewDesign: false,
    newDesignImage: null,
    imagePreviewUrl: null,

    // Expand Design ID Dialog State
    expandDesignDialog: false,
    expandDesignData: null, // { designId, categoryId, categoryName }
    includeTeeinForExpandDesign: false
  }),

  computed: {
    isGlobalLoading() {
      return this.isInitialLoad && (this.isLoading || this.isLoadingCategories);
    },

    loadingMessage() {
      if (this.isLoading && this.isLoadingCategories) {
        return 'Lade SKU Management...';
      } else if (this.isLoading) {
        return 'Lade SKUs...';
      } else if (this.isLoadingCategories) {
        return 'Lade Kategorien...';
      }
      return 'Lädt...';
    },

    /**
     * Count unique design IDs (skuList now contains pre-grouped Design ID groups)
     */
    uniqueDesignIdsCount() {
      // skuList is now an array of Design ID groups, so just return the length
      return this.skuList.length;
    },

    uniqueCategories() {
      const categories = [...new Set(this.skuList.map(sku => sku.category))];
      return categories.length;
    },
    
    categoryHasSkus() {
      if (!this.editingCategoryId) return false;
      // skuList is now an array of groups, need to check SKUs within each group
      return this.skuList.some(group =>
        group.skus.some(sku => sku.categoryId === this.editingCategoryId)
      );
    },

    categoryDialogTitle() {
      if (this.categoryStep === 2) {
        return 'Fehlende SKUs überprüfen';
      }
      return this.isEditMode ? 'Kategorie bearbeiten' : 'Neue Kategorie erstellen';
    },

    categoryNextButtonText() {
      if (this.categoryStep === 1 && this.isEditMode && this.categoryHasSkus) {
        return 'Weiter';
      }
      if (this.categoryStep === 2) {
        return 'Speichern & SKUs erstellen';
      }
      return this.isEditMode ? 'Speichern' : 'Erstellen';
    },

    /**
     * Validation rules for shortName field
     */
    shortNameRules() {
      return [
        v => !v || v.length <= 10 || 'Maximal 10 Zeichen erlaubt',
        v => !v || /^[A-Za-z0-9_-]*$/.test(v) || 'Nur A-Z, a-z, 0-9, _ und - erlaubt'
      ];
    },
    
    /**
     * Check if SKU has changes to save
     */
    hasSkuChanges() {
      if (!this.selectedSku) return false;
      return this.selectedSkuCategory !== this.selectedSku.categoryId;
    },

    /**
     * Validation rules for image upload
     */
    imageRules() {
      return [
        value => !value || value.size < 10485760 || 'Datei ist zu groß! Max. 10MB erlaubt',
        value => !value || ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'].includes(value.type) || 'Ungültiger Dateityp! Nur JPG, PNG, GIF, WebP, SVG erlaubt'
      ];
    }
  },

  async created() {
    await Promise.all([
      this.loadSkus(),
      this.loadCategories()
    ]);
    this.isInitialLoad = false;
  },

  methods: {
    /**
     * Lädt alle bestehenden SKUs vom Backend (optimized - pre-grouped by backend)
     */
    async loadSkus() {
      this.isLoading = true;

      try {
        const response = await WarehouseService.getSkusGroupedByDesignId();

        // Backend returns { groups: [], teeinGroups: [], categories: [] }
        // groups contain base SKUs, teeinGroups contain TEEIN SKUs
        this.skuList = response.data.groups || [];
        this.teeinSkuList = response.data.teeinGroups || [];

        console.log(`${this.skuList.length} Design ID groups and ${this.teeinSkuList.length} TEEIN groups loaded`);
      } catch (error) {
        console.error('Fehler beim Laden der SKUs:', error);
        this.showStatus('Fehler beim Laden der SKUs', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Erstellt neue SKUs für alle Farb-/Größenkombinationen der gewählten Kategorie
     * Design ID wird automatisch generiert
     * Wenn Flags ausgewählt sind, werden SKUs mit und ohne Flags erstellt
     */
    async createSku() {
      if (!this.selectedCategory) {
        this.showStatus('Bitte wählen Sie zuerst eine Kategorie', 'warning');
        return;
      }

      this.isCreating = true;

      try {
        const skuData = {
          categoryId: this.selectedCategory.id,
          flags: this.skuFlags.length > 0 ? this.skuFlags : []
        };

        const response = await WarehouseService.createSku(skuData);
        const result = response.data;
        
        // Format SKUs for display
        const displaySkus = result.skus.map(sku => ({
          id: sku.id,
          sku: sku.name,
          category: sku.category,
          categoryId: sku.categoryId,
          designId: sku.designId,
          flags: sku.flags || [],
          createdAt: sku.createdAt,
          active: sku.active,
          candidate: sku.candidate || false,
          posterStatus: sku.posterStatus || 'NOT_FOUND',
          translatorStatus: sku.translatorStatus === true
        }));
        
        // Add all SKUs to the list at the beginning
        this.skuList.unshift(...displaySkus);
        
        // Store for success message
        this.lastCreatedSkus = displaySkus;
        this.lastCreatedDesignId = result.designId;
        
        // Update statistics
        this.sessionCreated += result.count;

        this.showStatus(
          `${result.count} SKUs erfolgreich erstellt! (Design ID: ${result.designId})`,
          'success'
        );

        console.log(`${result.count} neue SKUs erstellt:`, displaySkus);
        
        // Reset flags
        this.skuFlags = [];
      } catch (error) {
        console.error('Fehler beim Erstellen der SKUs:', error);
        this.showStatus(
          error?.response?.data?.message || 'Fehler beim Erstellen der SKUs',
          'error'
        );
      } finally {
        this.isCreating = false;
      }
    },

    /**
     * Event Handler: Kategorie wurde ausgewählt
     */
    onCategorySelected(category) {
      console.log('Kategorie ausgewählt:', category);
      this.selectedCategory = category;
    },

    /**
     * Zeigt Details zu einer SKU im Dialog an
     */
    viewSkuDetails(sku) {
      this.selectedSku = sku;
      this.selectedSkuCategory = sku.categoryId;
      this.detailDialog = true;
    },
    
    /**
     * Saves changes to the SKU
     */
    async saveSkuChanges() {
      if (!this.selectedSku || !this.selectedSkuCategory) {
        this.showStatus('Keine SKU oder Kategorie ausgewählt', 'warning');
        return;
      }
      
      if (!this.selectedSku.id) {
        this.showStatus('SKU ID fehlt', 'error');
        return;
      }
      
      this.isSavingSku = true;
      
      try {
        await WarehouseService.updateSkuCategory(
          this.selectedSku.id,
          this.selectedSkuCategory
        );
        
        // Update local SKU list - search in the full list
        const skuIndex = this.skuList.findIndex(s => s.id === this.selectedSku.id);
        
        if (skuIndex !== -1) {
          const newCategory = this.categories.find(c => c.id === this.selectedSkuCategory);
          this.$set(this.skuList[skuIndex], 'categoryId', this.selectedSkuCategory);
          this.$set(this.skuList[skuIndex], 'category', newCategory ? newCategory.name : 'Unknown');
          
          // Also update the selectedSku to reflect changes
          this.selectedSku.categoryId = this.selectedSkuCategory;
          this.selectedSku.category = newCategory ? newCategory.name : 'Unknown';
        } else {
          console.warn('SKU not found in list after update:', this.selectedSku.id);
        }
        
        this.showStatus('SKU erfolgreich aktualisiert', 'success');
        this.detailDialog = false;
      } catch (error) {
        console.error('Fehler beim Aktualisieren der SKU:', error);
        this.showStatus(
          error?.response?.data?.message || 'Fehler beim Aktualisieren der SKU',
          'error'
        );
      } finally {
        this.isSavingSku = false;
      }
    },

    /**
     * Updates the candidate status of a SKU
     */
    async updateSkuCandidateStatus(skuId, candidate) {
      try {
        await WarehouseService.updateSkuCandidateStatus(skuId, candidate);
        
        // Update the local SKU list
        const skuIndex = this.skuList.findIndex(sku => sku.id === skuId);
        if (skuIndex !== -1) {
          this.$set(this.skuList[skuIndex], 'candidate', candidate);
        }
        
        this.showStatus(
          candidate ? 'SKU als KANDIDAT markiert' : 'SKU auf IN BENUTZUNG gesetzt',
          'success'
        );
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Kandidaten-Status:', error);
        this.showStatus('Fehler beim Aktualisieren des Status', 'error');
      }
    },

    /**
     * Handler for showing messages from child components
     */
    showMessage(message, type = 'info') {
      this.showStatus(message, type);
    },

    /**
     * Setzt das Formular zurück für eine weitere SKU-Erstellung
     */
    resetForm() {
      this.selectedCategory = null;
      this.skuFlags = [];
      this.lastCreatedSkus = [];
      this.lastCreatedDesignId = null;
      this.clearStatus();
    },

    /**
     * Schließt den SKU-Dialog und setzt das Formular zurück
     */
    closeSkuDialog() {
      this.skuDialog = false;
      this.resetForm();
    },

    /**
     * Zeigt eine Status-Nachricht an
     */
    showStatus(message, type = 'success') {
      this.statusMessage = message;
      this.statusType = type;

      // Auto-dismiss nach 5 Sekunden
      setTimeout(() => {
        this.clearStatus();
      }, 5000);
    },

    /**
     * Löscht die Status-Nachricht
     */
    clearStatus() {
      this.statusMessage = null;
    },

    /**
     * Formatiert ein Datum
     */
    formatDate(dateString) {
      if (!dateString) return '-';
      
      const date = new Date(dateString);
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      };
      
      return date.toLocaleDateString('de-DE', options);
    },

    // ===== CATEGORY MANAGEMENT =====

    /**
     * Lädt alle Kategorien vom Backend
     */
    async loadCategories() {
      this.isLoadingCategories = true;

      try {
        const response = await WarehouseService.getCategories();
        // Sort: Design-supporting categories first, then alphabetically within each group
        this.categories = (response.data || []).sort((a, b) => {
          // First, sort by supportsDesigns (true first)
          if (a.supportsDesigns !== b.supportsDesigns) {
            return b.supportsDesigns ? 1 : -1;
          }
          // Then alphabetically by name
          return a.name.localeCompare(b.name);
        });
        console.log(`${this.categories.length} Kategorien erfolgreich geladen`);
      } catch (error) {
        console.error('Fehler beim Laden der Kategorien:', error);
        this.showStatus('Fehler beim Laden der Kategorien', 'error');
      } finally {
        this.isLoadingCategories = false;
      }
    },

    /**
     * Handle next button in category workflow
     */
    async categoryStepNext() {
      if (this.categoryStep === 1) {
        // Validate form
        if (!this.$refs.categoryForm.validate()) {
          return;
        }

        // If editing category with SKUs, analyze for missing SKUs
        if (this.isEditMode && this.categoryHasSkus) {
          await this.analyzeChanges();
          this.categoryStep = 2;
        } else {
          // No SKUs, just save directly
          await this.saveCategory();
        }
      } else if (this.categoryStep === 2) {
        // Save and fill missing SKUs
        await this.saveCategoryAndFillMissing();
      }
    },

    /**
     * Go back in category workflow
     */
    categoryStepBack() {
      if (this.categoryStep === 2) {
        this.categoryStep = 1;
        this.affectedDesignIds = [];
        this.selectedDesignIdsToFill = [];
      }
    },

    /**
     * Analyze what SKUs would be missing after category changes
     */
    async analyzeChanges() {
      this.isAnalyzingCategory = true;
      this.affectedDesignIds = [];
      this.selectedDesignIdsToFill = [];

      try {
        // Get all Design IDs for this category
        const response = await WarehouseService.getDesignIds(this.editingCategoryId);
        const designIds = response.data;

        if (designIds.length === 0) {
          return; // No design IDs, nothing to analyze
        }

        // Analyze each Design ID with the NEW colors/sizes
        for (const designId of designIds) {
          // Calculate what would be missing with new colors/sizes
          const analysis = await this.analyzeMissingForDesignId(designId);
          
          if (analysis.missing > 0) {
            this.affectedDesignIds.push({
              id: designId,
              missing: analysis.missing,
              total: analysis.total
            });
            // Auto-select all affected design IDs
            this.selectedDesignIdsToFill.push(designId);
          }
        }

      } catch (error) {
        console.error('Fehler beim Analysieren:', error);
        this.showStatus('Fehler beim Analysieren der Änderungen', 'error');
      } finally {
        this.isAnalyzingCategory = false;
      }
    },

    /**
     * Analyze missing SKUs for a specific design ID with NEW category data
     */
    async analyzeMissingForDesignId(designId) {
      try {
        // Use backend API to analyze current state
        const response = await WarehouseService.analyzeMissingSkus(
          this.editingCategoryId,
          designId
        );
        
        // Calculate with NEW colors/sizes (simple estimation)
        const newTotal = this.newCategory.colors.length * this.newCategory.sizes.length * 2;
        const currentTotal = response.data.total;
        const currentExisting = response.data.existing;
        
        // Estimate missing based on difference
        const estimatedMissing = Math.max(0, newTotal - currentExisting);

        return {
          total: newTotal,
          existing: currentExisting,
          missing: estimatedMissing
        };
      } catch (error) {
        console.error('Error analyzing design ID:', error);
        // Fallback to simple calculation
        const totalExpected = this.newCategory.colors.length * this.newCategory.sizes.length * 2;
        return {
          total: totalExpected,
          existing: 0,
          missing: totalExpected
        };
      }
    },

    /**
     * Save category and fill selected missing SKUs
     */
    async saveCategoryAndFillMissing() {
      this.isCreatingCategory = true;

      try {
        // First, save the category
        const categoryData = {
          category: this.newCategory.name,
          shortName: this.newCategory.shortName || null,
          colors: this.newCategory.colors || [],
          sizes: this.newCategory.sizes || [],
          supportsDesigns: this.newCategory.supportsDesigns !== false // Default to true
        };

        const response = await WarehouseService.updateCategory(this.editingCategoryId, categoryData);
        const updatedCategory = response.data;
        
        // Update in list
        const index = this.categories.findIndex(cat => cat.id === this.editingCategoryId);
        if (index !== -1) {
          this.$set(this.categories, index, updatedCategory);
        }

        // Now fill missing SKUs for selected Design IDs
        let totalCreated = 0;
        
        for (const designId of this.selectedDesignIdsToFill) {
          try {
            const options = {
              flags: this.fillWithTeeingInWorkflow ? ['TEEIN'] : []
            };

            const fillResponse = await WarehouseService.fillMissingSkus(
              this.editingCategoryId,
              designId,
              options
            );

            totalCreated += fillResponse.data.created;
          } catch (error) {
            console.error(`Fehler beim Füllen von ${designId}:`, error);
          }
        }

        // Reload SKUs to get the new ones
        await this.loadSkus();

        this.showStatus(
          `Kategorie aktualisiert! ${totalCreated} fehlende SKUs erstellt.`,
          'success'
        );

        this.closeCategoryDialog();
      } catch (error) {
        console.error('Fehler beim Speichern:', error);
        this.showStatus(
          error?.response?.data?.message || 'Fehler beim Speichern der Kategorie',
          'error'
        );
      } finally {
        this.isCreatingCategory = false;
      }
    },

    /**
     * Simple save for new categories or edits without SKUs
     */
    async saveCategory() {
      this.isCreatingCategory = true;

      try {
        const categoryData = {
          category: this.newCategory.name,
          shortName: this.newCategory.shortName || null,
          colors: this.newCategory.colors || [],
          sizes: this.newCategory.sizes || [],
          supportsDesigns: this.newCategory.supportsDesigns !== false // Default to true
        };

        if (this.isEditMode) {
          // Update existing category
          const response = await WarehouseService.updateCategory(this.editingCategoryId, categoryData);
          const updatedCategory = response.data;
          
          // Update in list
          const index = this.categories.findIndex(cat => cat.id === this.editingCategoryId);
          if (index !== -1) {
            this.$set(this.categories, index, updatedCategory);
          }

          this.showStatus(
            `Kategorie "${updatedCategory.name}" erfolgreich aktualisiert!`,
            'success'
          );
        } else {
          // Create new category
          const response = await WarehouseService.createCategory(categoryData);
          const createdCategory = response.data;
          
          // Add to list
          this.categories.push(createdCategory);

          this.showStatus(
            `Kategorie "${createdCategory.name}" erfolgreich erstellt!`,
            'success'
          );
        }

        this.closeCategoryDialog();
      } catch (error) {
        console.error('Fehler beim Speichern der Kategorie:', error);
        this.showStatus(
          error?.response?.data?.message || 'Fehler beim Speichern der Kategorie',
          'error'
        );
      } finally {
        this.isCreatingCategory = false;
      }
    },

    /**
     * Öffnet den Dialog zum Bearbeiten einer Kategorie
     */
    editCategory(category) {
      this.isEditMode = true;
      this.editingCategoryId = category.id;
      this.originalCategory = { ...category };
      this.newCategory = {
        name: category.name,
        shortName: category.shortName || '',
        colors: [...category.colors],
        sizes: [...category.sizes],
        supportsDesigns: category.supportsDesigns !== false // Default to true if undefined
      };
      this.categoryStep = 1;
      this.affectedDesignIds = [];
      this.selectedDesignIdsToFill = [];
      this.fillWithTeeingInWorkflow = false;
      this.categoryDialog = true;
    },

    /**
     * Schließt den Kategorie-Dialog und setzt das Formular zurück
     */
    closeCategoryDialog() {
      this.categoryDialog = false;
      this.isEditMode = false;
      this.editingCategoryId = null;
      this.originalCategory = null;
      this.categoryStep = 1;
      this.affectedDesignIds = [];
      this.selectedDesignIdsToFill = [];
      this.fillWithTeeingInWorkflow = false;
      this.newCategory = {
        name: '',
        shortName: '',
        colors: [],
        sizes: [],
        supportsDesigns: true
      };

      if (this.$refs.categoryForm) {
        this.$refs.categoryForm.reset();
      }
    },

    /**
     * Entfernt eine Farbe aus der Liste
     */
    removeColor(index) {
      this.newCategory.colors.splice(index, 1);
    },

    /**
     * Entfernt eine Größe aus der Liste
     */
    removeSize(index) {
      this.newCategory.sizes.splice(index, 1);
    },

    /**
     * Sanitizes shortName input to remove invalid characters
     */
    sanitizeShortName() {
      if (this.newCategory.shortName) {
        // Remove all characters that are not A-Z, a-z, 0-9, _, or -
        this.newCategory.shortName = this.newCategory.shortName
          .replace(/[^A-Za-z0-9_-]/g, '')
          .substring(0, 10)
          .toUpperCase(); // Convert to uppercase for consistency
      }
    },

    // ===== NEW DESIGN ID CREATION =====

    /**
     * Opens dialog for creating a new Design ID
     */
    openNewDesignIdDialog() {
      this.selectedCategoryForNewDesign = null;
      this.includeTeeinForNewDesign = false;
      this.newDesignImage = null;
      this.imagePreviewUrl = null;
      this.newDesignIdDialog = true;
    },

    /**
     * Closes new Design ID dialog
     */
    closeNewDesignIdDialog() {
      this.newDesignIdDialog = false;
      this.selectedCategoryForNewDesign = null;
      this.includeTeeinForNewDesign = false;
      this.newDesignImage = null;
      this.imagePreviewUrl = null;
    },

    /**
     * Handle image selection and create preview
     */
    onImageSelected(file) {
      // Clear previous preview
      if (this.imagePreviewUrl) {
        URL.revokeObjectURL(this.imagePreviewUrl);
        this.imagePreviewUrl = null;
      }

      // Create new preview if file is selected
      if (file) {
        this.imagePreviewUrl = URL.createObjectURL(file);
      }
    },

    /**
     * Creates a new Design ID with selected category
     */
    async createNewDesignId() {
      if (!this.selectedCategoryForNewDesign) {
        this.showStatus('Bitte wählen Sie eine Kategorie', 'warning');
        return;
      }

      this.isCreating = true;

      try {
        const skuData = {
          categoryId: this.selectedCategoryForNewDesign,
          flags: this.includeTeeinForNewDesign ? ['TEEIN'] : []
        };

        const response = await WarehouseService.createSku(skuData);
        const result = response.data;

        // Upload image if one was selected
        if (this.newDesignImage) {
          try {
            await WarehouseService.uploadDesignMedia(result.designId, this.newDesignImage);
            console.log(`Image uploaded for Design ID ${result.designId}`);
          } catch (uploadError) {
            console.error('Fehler beim Hochladen des Bildes:', uploadError);
            this.showStatus(
              `Design ID erstellt, aber Bild-Upload fehlgeschlagen: ${uploadError?.response?.data?.error || uploadError.message}`,
              'warning'
            );
          }
        }

        const teeinText = this.includeTeeinForNewDesign ? ' (inkl. TEEIN Varianten)' : '';
        const imageText = this.newDesignImage ? ' mit Bild' : '';
        this.showStatus(
          `${result.count} SKUs für neue Design ID ${result.designId} erfolgreich erstellt!${teeinText}${imageText}`,
          'success'
        );

        // Reload data
        await this.loadSkus();

        this.closeNewDesignIdDialog();
      } catch (error) {
        console.error('Fehler beim Erstellen der Design ID:', error);
        this.showStatus(
          error?.response?.data?.error || 'Fehler beim Erstellen der Design ID',
          'error'
        );
      } finally {
        this.isCreating = false;
      }
    },

    // ===== EXPAND EXISTING DESIGN ID =====

    /**
     * Handles adding a category to an existing Design ID
     */
    handleAddCategoryToDesign(data) {
      this.expandDesignData = data;
      this.includeTeeinForExpandDesign = false;
      this.expandDesignDialog = true;
    },

    /**
     * Closes expand design dialog
     */
    closeExpandDesignDialog() {
      this.expandDesignDialog = false;
      this.expandDesignData = null;
      this.includeTeeinForExpandDesign = false;
    },

    /**
     * Confirms and executes adding category to Design ID
     */
    async confirmExpandDesign() {
      if (!this.expandDesignData) return;

      this.isCreating = true;

      try {
        const { designId, categoryId, categoryName } = this.expandDesignData;

        const options = {
          flags: this.includeTeeinForExpandDesign ? ['TEEIN'] : []
        };

        const response = await WarehouseService.fillMissingSkus(
          categoryId,
          designId,
          options
        );

        const teeinText = this.includeTeeinForExpandDesign ? ' (inkl. TEEIN Varianten)' : '';
        this.showStatus(
          `${response.data.created} SKUs für ${categoryName} in Design ID ${designId} erstellt!${teeinText}`,
          'success'
        );

        // Reload data
        await this.loadSkus();

        this.closeExpandDesignDialog();
      } catch (error) {
        console.error('Fehler beim Erweitern der Design ID:', error);
        this.showStatus(
          error?.response?.data?.error || 'Fehler beim Erweitern der Design ID',
          'error'
        );
      } finally {
        this.isCreating = false;
      }
    }
  }
};
</script>

<style scoped>
/* Page Wrapper - Fills Viewport */
.page-wrapper {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Status Banner */
.status-banner {
  margin: 12px;
  margin-bottom: 0;
}

/* Tables Container - Fills Remaining Space */
.tables-container {
  flex: 1;
  display: flex;
  gap: 16px;
  padding: 16px;
  overflow: hidden;
}

/* Table Columns */
.table-column {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.categories-column {
  flex: 0 0 auto;
  width: fit-content;
  min-width: 350px;
  max-width: 600px;
}

.skus-column {
  flex: 1;
  min-width: 0;
}

/* Table Card */
.table-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.table-card:hover {
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
}

/* Table Header */
.table-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 600;
  flex-shrink: 0;
  letter-spacing: 0.3px;
}

.table-header .v-btn {
  transition: all 0.3s ease;
}

.table-header .v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Table Body - Scrollable */
.table-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.table-body >>> .h-100 {
  height: 100% !important;
}

/* Categories Scroll Area */
.categories-scroll {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  scroll-behavior: smooth;
}

.categories-scroll::-webkit-scrollbar {
  width: 8px;
}

.categories-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.categories-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #1976d2 0%, #1565c0 100%);
  border-radius: 10px;
}

.categories-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #1565c0 0%, #0d47a1 100%);
}

/* Category Item */
.category-item {
  margin-bottom: 16px;
  border-radius: 8px !important;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid #e0e0e0 !important;
  background: white;
}

.category-item:last-child {
  margin-bottom: 0;
}

.category-item:hover {
  box-shadow: 0 6px 16px rgba(25, 118, 210, 0.2) !important;
  transform: translateY(-2px);
  border-color: #1976d2 !important;
  background: #f5f9fc;
}

.category-item .v-card__text {
  padding: 12px !important;
}

.category-name {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 4px;
}

.category-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.meta-label {
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
}

/* Flags Section */
.flags-section {
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.flags-label {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.flags-hint {
  font-size: 11px;
  color: #6c757d;
  font-style: italic;
}

/* Alert Styling */
.v-alert {
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-banner {
  border-radius: 8px !important;
}

/* Dialog */
.v-dialog .v-card {
  border-radius: 12px !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.v-card-title.primary,
.v-card-title.success {
  font-size: 16px !important;
  font-weight: 600 !important;
  padding: 16px 24px !important;
  letter-spacing: 0.3px;
}

/* Modern Dialog Input Styling */
.v-dialog .v-text-field >>> fieldset,
.v-dialog .v-select >>> fieldset,
.v-dialog .v-combobox >>> fieldset {
  border-color: #e0e0e0 !important;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.v-dialog .v-input--is-focused >>> fieldset {
  border-color: #1976d2 !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

.v-dialog .v-text-field >>> .v-input__control,
.v-dialog .v-select >>> .v-input__control,
.v-dialog .v-combobox >>> .v-input__control {
  border-radius: 8px !important;
}

/* Detail Dialog - Information Display Style */
.detail-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 600;
  color: #2c3e50;
  min-width: 140px;
  font-size: 14px;
}

.detail-value {
  flex: 1;
  color: #555;
  font-size: 14px;
}

.editable-row {
  align-items: center;
  background: #f8f9fa;
  padding: 16px 12px;
  border-radius: 6px;
  border: 1px dashed #cbd5e0;
  transition: all 0.3s ease;
}

.editable-row:hover {
  background: #e3f2fd;
  border-color: #1976d2;
}

.detail-value-editable {
  flex: 1;
}

.category-select-compact {
  max-width: 100%;
}

.category-select-compact >>> .v-input__control {
  min-height: 36px !important;
}

.category-select-compact >>> fieldset {
  border-color: #e0e0e0 !important;
  border-radius: 6px !important;
  transition: all 0.3s ease;
}

.category-select-compact >>> .v-input--is-focused fieldset {
  border-color: #1976d2 !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

/* File Upload Section */
.file-upload-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.image-preview {
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 2px dashed #e0e0e0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-preview .v-image {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Responsive */
@media (max-width: 960px) {
  .tables-container {
    flex-direction: column;
    overflow-y: auto;
  }

  .table-column {
    flex: none !important;
    min-height: 400px;
  }

  .categories-column {
    flex: none !important;
  }
}

@media (max-width: 600px) {
  .tables-container {
    padding: 8px;
    gap: 8px;
  }
}
</style>


